import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import EnhancedSimulationResults from './EnhancedSimulationResults';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DemoResults = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { type } = useParams(); // excellent, moderate, or poor
  const [demoData, setDemoData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  console.log('DemoResults rendered');
  console.log('URL pathname:', location.pathname);
  console.log('Type from useParams:', type);

  useEffect(() => {
    const loadDemoData = async () => {
      setLoading(true);
      setDemoData(null);
      
      try {
        const response = await axios.get(`${API}/demo/simulations`);
        console.log('Demo type from URL:', type);
        console.log('Available demos:', Object.keys(response.data));
        
        const selectedDemo = response.data[type || 'excellent'];
        console.log('Selected demo product:', selectedDemo?.product?.name);
        
        // Format demo data to match what EnhancedSimulationResults expects
        const formattedDemo = {
          simulation: selectedDemo,
          concept: {
            name: selectedDemo.product.name,
            description: selectedDemo.product.description
          }
        };
        
        setDemoData(formattedDemo);
      } catch (error) {
        console.error('Failed to load demo data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadDemoData();
  }, [type]);

  if (loading || !demoData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50">
      {/* Demo Banner */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-semibold">
              DEMO MODE
            </span>
            <span className="text-sm">
              This is a sample analysis. Sign up to test your own products!
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="text-white hover:bg-white/20"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </div>

      {/* Product Type Selector */}
      <div className="bg-white border-b border-slate-200 py-6">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-lg font-semibold mb-4 text-slate-800">Select a Product Type to Analyze:</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => navigate('/demo/excellent')}
              className={`p-6 rounded-xl border-2 transition-all text-left ${
                type === 'excellent'
                  ? 'border-green-500 bg-green-50 shadow-lg'
                  : 'border-slate-200 hover:border-green-300 hover:shadow-md'
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-4 h-4 rounded-full bg-green-500"></div>
                <span className="font-bold text-lg">Excellent Product</span>
              </div>
              <p className="text-sm text-slate-600 mb-2">Premium Wireless Headphones</p>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-green-600">4.28</span>
                <span className="text-sm text-slate-500">/ 5.0 intent score</span>
              </div>
              <p className="text-xs text-slate-500 mt-2">80% high purchase intent (scores 4-5)</p>
            </button>

            <button
              onClick={() => navigate('/demo/moderate')}
              className={`p-6 rounded-xl border-2 transition-all text-left ${
                type === 'moderate'
                  ? 'border-yellow-500 bg-yellow-50 shadow-lg'
                  : 'border-slate-200 hover:border-yellow-300 hover:shadow-md'
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
                <span className="font-bold text-lg">Moderate Product</span>
              </div>
              <p className="text-sm text-slate-600 mb-2">Smart Security Camera System</p>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-yellow-600">3.02</span>
                <span className="text-sm text-slate-500">/ 5.0 intent score</span>
              </div>
              <p className="text-xs text-slate-500 mt-2">35% high purchase intent (scores 4-5)</p>
            </button>

            <button
              onClick={() => navigate('/demo/poor')}
              className={`p-6 rounded-xl border-2 transition-all text-left ${
                type === 'poor'
                  ? 'border-red-500 bg-red-50 shadow-lg'
                  : 'border-slate-200 hover:border-red-300 hover:shadow-md'
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-4 h-4 rounded-full bg-red-500"></div>
                <span className="font-bold text-lg">Poor Product</span>
              </div>
              <p className="text-sm text-slate-600 mb-2">Shoelace Subscription Box</p>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-red-600">1.72</span>
                <span className="text-sm text-slate-500">/ 5.0 intent score</span>
              </div>
              <p className="text-xs text-slate-500 mt-2">3% high purchase intent (scores 4-5)</p>
            </button>
          </div>
        </div>
      </div>

      {/* Render actual simulation results component */}
      <EnhancedSimulationResults 
        key={type || 'excellent'}
        simulation={demoData.simulation}
        concept={demoData.concept}
        isDemo={true}
      />

      {/* CTA at bottom */}
      <div className="bg-gradient-to-r from-indigo-600 to-cyan-600 py-12">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Test Your Own Products?
          </h2>
          <p className="text-indigo-100 text-lg mb-6">
            Get instant AI-powered insights for your product concepts in minutes
          </p>
          <div className="flex gap-4 justify-center">
            <Button
              onClick={() => navigate('/pricing')}
              size="lg"
              className="bg-white text-indigo-600 hover:bg-indigo-50 px-8"
            >
              View Pricing
            </Button>
            <Button
              onClick={() => {
                const demoTypes = ['excellent', 'moderate', 'poor'];
                const currentIndex = demoTypes.indexOf(type || 'excellent');
                const nextIndex = (currentIndex + 1) % demoTypes.length;
                navigate(`/demo/${demoTypes[nextIndex]}`);
              }}
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10"
            >
              Try Another Demo
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoResults;
